import React, { useState, useEffect } from "react";
import { Box, Paper, TextField, Button, Typography, Alert, CircularProgress, Link, Stepper, Step, StepLabel, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { WhatsApp as WhatsAppIcon, Email as EmailIcon } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Footer from "../components/Footer";
import SupportFab from "../components/SupportFab";
import { setSession } from "../utils/auth";

function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (params.get("reason") === "idle") {
      setInfo("Sua sessão expirou por inatividade. Faça login novamente.");
    }
  }, [params]);

  // Estado para recuperacao de senha
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(0); // 0: email, 1: codigo, 2: nova senha
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [maskedContact, setMaskedContact] = useState("");
  const [success, setSuccess] = useState("");
  const [sendMethod, setSendMethod] = useState("whatsapp"); // whatsapp ou email

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post("/api/customer-portal/login", { email, password });
      setSession(res.data.access_token, res.data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const res = await axios.post("/api/customer-portal/forgot-password", {
        email: forgotEmail,
        method: sendMethod
      });
      if (res.data.success) {
        setMaskedContact(res.data.maskedPhone || res.data.maskedEmail || "");
        setSuccess(res.data.message);
        setForgotStep(1);
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao solicitar recuperacao");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError("");
    setSuccess("");
    if (newPassword !== confirmPassword) {
      setError("As senhas nao conferem");
      return;
    }
    if (newPassword.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post("/api/customer-portal/reset-password", {
        email: forgotEmail,
        code: resetCode,
        newPassword: newPassword
      });
      if (res.data.success) {
        setSuccess("Senha alterada com sucesso! Voce ja pode fazer login.");
        setForgotStep(2);
        // Voltar para login apos 3 segundos
        setTimeout(() => {
          setForgotMode(false);
          setForgotStep(0);
          setForgotEmail("");
          setResetCode("");
          setNewPassword("");
          setConfirmPassword("");
          setSendMethod("whatsapp");
        }, 3000);
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  const resetForgotMode = () => {
    setForgotMode(false);
    setForgotStep(0);
    setForgotEmail("");
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
    setSendMethod("whatsapp");
  };

  // Modo de recuperacao de senha
  if (forgotMode) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5" }}>
        <Paper sx={{ p: 4, maxWidth: 450, width: "100%" }}>
          <Typography variant="h5" align="center" gutterBottom sx={{ color: "#1976d2" }}>
            Recuperar Senha
          </Typography>

          <Stepper activeStep={forgotStep} sx={{ mb: 3 }}>
            <Step><StepLabel>Email</StepLabel></Step>
            <Step><StepLabel>Codigo</StepLabel></Step>
            <Step><StepLabel>Concluido</StepLabel></Step>
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {forgotStep === 0 && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Digite seu email cadastrado e escolha como deseja receber o codigo de recuperacao.
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                margin="normal"
                required
                autoFocus
              />

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Enviar codigo via:
              </Typography>
              <ToggleButtonGroup
                value={sendMethod}
                exclusive
                onChange={(e, v) => v && setSendMethod(v)}
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton value="whatsapp" sx={{ py: 1.5 }}>
                  <WhatsAppIcon sx={{ mr: 1, color: sendMethod === "whatsapp" ? "#25D366" : "inherit" }} />
                  WhatsApp
                </ToggleButton>
                <ToggleButton value="email" sx={{ py: 1.5 }}>
                  <EmailIcon sx={{ mr: 1, color: sendMethod === "email" ? "#1976d2" : "inherit" }} />
                  Email
                </ToggleButton>
              </ToggleButtonGroup>

              <Button
                fullWidth
                variant="contained"
                onClick={handleForgotPassword}
                disabled={loading || !forgotEmail}
                size="large"
              >
                {loading ? <CircularProgress size={24} /> : "Enviar Codigo"}
              </Button>
            </>
          )}

          {forgotStep === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Codigo enviado para {maskedContact || (sendMethod === "whatsapp" ? "seu WhatsApp" : "seu email")}
              </Alert>
              <TextField
                fullWidth
                label="Codigo de 6 digitos"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                margin="normal"
                required
                autoFocus
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 20, letterSpacing: 6 } }}
              />
              <TextField
                fullWidth
                label="Nova Senha"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                helperText="Minimo 6 caracteres"
              />
              <TextField
                fullWidth
                label="Confirmar Nova Senha"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleResetPassword}
                disabled={loading || !resetCode || !newPassword || !confirmPassword}
                sx={{ mt: 2 }}
                size="large"
              >
                {loading ? <CircularProgress size={24} /> : "Alterar Senha"}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => { setForgotStep(0); setError(""); setSuccess(""); }}
                sx={{ mt: 1 }}
              >
                Reenviar codigo
              </Button>
            </>
          )}

          {forgotStep === 2 && (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" color="success.main">
                Senha alterada com sucesso!
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Redirecionando para login...
              </Typography>
            </Box>
          )}

          <Button fullWidth variant="text" onClick={resetForgotMode} sx={{ mt: 2 }}>
            Voltar para Login
          </Button>
        </Paper>
      </Box>
    );
  }

  // Modo de login normal
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: "100%" }}>
        <Box sx={{ textAlign: "center", mb: 3, display: 'flex', justifyContent: 'center' }}>
          <img src='/logo-cliente.png' alt='Portal do Cliente — Otimize Informatica' style={{ maxWidth: 280, height: 'auto' }} />
        </Box>

        {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleLogin}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoFocus
          />
          <TextField
            fullWidth
            label="Senha"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading}
            size="large"
            sx={{ mt: 2, mb: 1 }}
          >
            {loading ? <CircularProgress size={24} /> : "Entrar"}
          </Button>
        </form>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => setForgotMode(true)}
            sx={{ color: "#1976d2" }}
          >
            Esqueci minha senha
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate("/cadastro")}
            sx={{ color: "#1976d2" }}
          >
            Criar conta
          </Link>
        </Box>
      </Paper>
      </Box>
      <SupportFab />
      <Footer />
    </Box>
  );
}

export default Login;
